import {ControlledDatepickerValue} from '@hm/fundamental'
import moment from 'moment-timezone'

import {
  DefinitionLookup,
  DefinitionMaps,
  KpiValueLookup,
  RoleFilterValue,
  SlaCommentDefinitionRecord,
  SlaCommentRecord,
  SlaCommentValueRecord,
  TabTypeDto,
  TranslateFn
} from '../types'

export const DEFAULT_START_MONTH = 1
export const DEFAULT_END_MONTH = 12
export const COMMENT_CONTEXT_MONTH_FORMAT = 'MMMM YYYY'

export const isMomentInput = (value: unknown): value is moment.MomentInput => {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value instanceof Date ||
    moment.isMoment(value)
  )
}

export const toMoment = (value: unknown, timezone: string): moment.Moment | null => {
  if (!isMomentInput(value)) {
    return null
  }

  const instance = moment(value)
  return instance.isValid() ? instance.tz(timezone) : null
}

export const normalizeDatepickerRange = (
  value: ControlledDatepickerValue
): [Date | null, Date | null] => {
  if (Array.isArray(value)) {
    const [first, second] = value

    if (first && second && first > second) {
      return [second, first]
    }

    return [first ?? null, second ?? null]
  }

  if (value instanceof Date) {
    return [value, value]
  }

  return [null, null]
}

export const buildDateMoment = (value: Date | null, timezone: string): moment.Moment | null => {
  if (!value) {
    return null
  }

  const base = moment(value)

  if (!base.isValid()) {
    return null
  }

  return moment.tz({year: base.year(), month: base.month(), day: base.date()}, timezone)
}

export const getFallbackDefinitionLookup = (
  definitionId: string | undefined,
  valueLookup: KpiValueLookup | undefined,
  maps: DefinitionMaps
): DefinitionLookup | undefined => {
  if (definitionId) {
    return maps.definitionMap.get(definitionId)
  }

  if (valueLookup?.definitionId) {
    return maps.definitionMap.get(valueLookup.definitionId)
  }

  return undefined
}

export const getResolvedTabType = (
  definitionTabType: TabTypeDto | null | undefined,
  fallbackDefinitionLookup: DefinitionLookup | undefined,
  valueLookup: KpiValueLookup | undefined
): TabTypeDto | undefined => {
  return definitionTabType ?? fallbackDefinitionLookup?.tabType ?? valueLookup?.tabType
}

export const getResolvedMonth = (
  monthValue: unknown,
  fallbackMonth: number | undefined
): number | undefined => {
  if (typeof monthValue === 'number') {
    return monthValue
  }

  return fallbackMonth
}

export const getResolvedYear = (
  definitionYear: unknown,
  fallbackYear: number | undefined
): number | undefined => {
  if (typeof definitionYear === 'number') {
    return definitionYear
  }

  return typeof fallbackYear === 'number' ? fallbackYear : undefined
}

export const getContextMoment = (
  year: number | undefined,
  month: number | undefined,
  timezone: string
): moment.Moment | null => {
  if (!year || !month) {
    return null
  }

  return moment.tz({year, month: month - 1, day: 1}, timezone)
}

export const getDefinitionName = (
  definitionName: unknown,
  fallbackDefinitionLookup: DefinitionLookup | undefined,
  valueLookup: KpiValueLookup | undefined,
  unknownLabel: string
): string => {
  if (typeof definitionName === 'string' && definitionName.trim().length > 0) {
    return definitionName
  }

  if (fallbackDefinitionLookup?.definition.name) {
    return fallbackDefinitionLookup.definition.name
  }

  if (valueLookup?.definitionName) {
    return valueLookup.definitionName
  }

  return unknownLabel
}

export const doesDefinitionMatchFilter = (
  resolvedDefinitionId: string | undefined,
  definitionName: string,
  kpiFilter: string,
  selectedDefinitionName?: string
): boolean => {
  if (resolvedDefinitionId) {
    return resolvedDefinitionId === kpiFilter
  }

  if (!selectedDefinitionName) {
    return false
  }

  return definitionName === selectedDefinitionName
}

export const getUnit = (
  definitionUnit: unknown,
  fallbackDefinitionLookup: DefinitionLookup | undefined,
  valueLookup: KpiValueLookup | undefined
): string | undefined => {
  if (typeof definitionUnit === 'string' && definitionUnit.length > 0) {
    return definitionUnit
  }

  if (fallbackDefinitionLookup?.definition.slaTarget.unit) {
    return fallbackDefinitionLookup.definition.slaTarget.unit
  }

  return valueLookup?.unit
}

export const getNumericValue = (
  value: unknown,
  fallbackValue: number | undefined
): number | undefined => {
  if (typeof value === 'number') {
    return value
  }

  return typeof fallbackValue === 'number' ? fallbackValue : undefined
}

export const formatValueLabel = (
  value: number | undefined,
  unit: string | undefined
): string | undefined => {
  if (value === undefined) {
    return undefined
  }

  return `${value}${unit ? ` ${unit}` : ''}`
}

export const getDisplayUserName = (userName: unknown, unknownLabel: string): string => {
  const normalized = typeof userName === 'string' ? userName.trim() : ''
  return normalized.length > 0 ? normalized : unknownLabel
}

export const matchesSearchTerm = (values: string[], searchValue: string): boolean => {
  if (searchValue.length === 0) {
    return true
  }

  return values.some((text) => text.toLowerCase().includes(searchValue))
}

export const buildItemKey = (valueId: string, timestamp: unknown, commentBody: string): string => {
  const timestampPart =
    typeof timestamp === 'string' || typeof timestamp === 'number'
      ? String(timestamp)
      : timestamp instanceof Date
        ? timestamp.toISOString()
        : ''

  const key = [valueId, timestampPart, commentBody].filter((part) => part).join('-')
  return key.length > 0 ? key : valueId
}

export const getCommentDefinition = (
  definition: SlaCommentDefinitionRecord | null | undefined
): SlaCommentDefinitionRecord | null => {
  return definition ?? null
}

export const getDefinitionId = (
  definition: SlaCommentDefinitionRecord | null
): string | undefined => {
  return typeof definition?.id === 'string' ? definition.id : undefined
}

export const getDefinitionTabType = (
  definition: SlaCommentDefinitionRecord | null
): TabTypeDto | undefined => {
  return definition?.tabType ?? undefined
}

export const getResolvedDefinitionId = (
  definitionId: string | undefined,
  fallbackDefinitionId: string | undefined
): string | undefined => {
  return definitionId ?? fallbackDefinitionId
}

export const normalizeUnit = (unit: unknown): string => {
  return typeof unit === 'string' ? unit : ''
}

export const getRoleLabel = (
  tabType: TabTypeDto | undefined,
  t: TranslateFn
): string | undefined => {
  return tabType ? t(`tabs.${tabType}`) : undefined
}

export const getCommentBody = (commentText: unknown): string => {
  return typeof commentText === 'string' ? commentText : ''
}

export const buildSearchValues = (
  userName: string,
  commentBody: string,
  definitionName: string,
  roleLabel: string | undefined,
  contextLabel: string,
  valueLabel: string | undefined
): string[] => {
  return [userName, commentBody, definitionName, roleLabel ?? '', contextLabel, valueLabel ?? '']
}

export const extractCommentValue = (
  comment: SlaCommentRecord
): {commentValue: SlaCommentValueRecord; valueId: string} | null => {
  if (!comment || typeof comment !== 'object') {
    return null
  }

  const rawValue = comment.value

  if (!rawValue || typeof rawValue !== 'object') {
    return null
  }

  const commentValue: SlaCommentValueRecord = rawValue
  const valueId = typeof commentValue.id === 'string' ? commentValue.id : undefined

  if (!valueId) {
    return null
  }

  return {commentValue, valueId}
}

export const doesRoleMatchFilter = (
  roleFilter: RoleFilterValue,
  tabType: TabTypeDto | undefined
): boolean => {
  if (!tabType) {
    return false
  }

  return roleFilter === 'all' || tabType === roleFilter
}

export const isCommentVisibleForKpi = (
  resolvedDefinitionId: string | undefined,
  definitionName: string,
  kpiFilter: string,
  selectedDefinitionName?: string
): boolean => {
  if (kpiFilter === 'all') {
    return true
  }

  return doesDefinitionMatchFilter(
    resolvedDefinitionId,
    definitionName,
    kpiFilter,
    selectedDefinitionName
  )
}
